import CreditCardIcon from "@redotech/redo-icons/credit-card-01.svg";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import DiscoverIcon from "@redotech/redo-icons/payment-method-discover.svg";
import MastercardIcon from "@redotech/redo-icons/payment-method-mastercard.svg";
import VisaIcon from "@redotech/redo-icons/payment-method-visa.svg";
import { trackerStatusColors } from "@redotech/redo-merchant-app-common/order";
import {
  OrderDetailsInfo,
  Transaction,
} from "@redotech/redo-merchant-app-common/order-details-info";
import {
  getCountryCode,
  getProvinceCode,
} from "@redotech/redo-model/draft-return/util";
import { LineItem, ShopifyAddress } from "@redotech/redo-model/order";
import { FulfillmentGroupTagSource, TagKind } from "@redotech/redo-model/tag";
import { prettyTrackerStatus } from "@redotech/redo-model/trackable/delivery-estimation";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { MoneyText } from "@redotech/redo-web/fx/money-text";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import { PillTheme } from "@redotech/redo-web/pill";
import { Text } from "@redotech/redo-web/text";
import { getDateString } from "@redotech/util/date-format";
import { JSXElementConstructor, memo } from "react";
import { TagList } from "../../common/tag-list";
import * as styles from "./outbound-order.module.css";

export const OrderSummary = memo(function OrderSummary({
  orderInfo,
}: {
  orderInfo: OrderDetailsInfo;
}) {
  const statuses = [];
  for (const tracker of orderInfo.order?.trackers ?? []) {
    statuses.push(
      <div key={prettyTrackerStatus[tracker._tracker.status]}>
        <RedoBadge
          color={trackerStatusColors[tracker._tracker.status]}
          segmentLeading={{ type: "icon", Icon: DotIcon }}
          size="xs"
          text={prettyTrackerStatus[tracker._tracker.status]}
          type="primary"
        />
      </div>,
    );
  }

  const renderedTags = (orderInfo.order?.shopify?.tags?.split(",") || [])
    .filter(Boolean)
    .map((tag: string) => ({
      name: tag,
      pillTheme: PillTheme.NORMAL,
      source: FulfillmentGroupTagSource.MERCHANT,
      kind: TagKind.FULFILLMENT_GROUP,
    }));

  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      pr="sm"
    >
      <Flex flexDirection="column" gap="xl" p="lg">
        <Text fontSize="sm">Order summary</Text>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Order date
          </Text>
          <Text fontSize="xs">
            {getDateString(new Date(orderInfo.createdAt))}
          </Text>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Order number
          </Text>
          <Text fontSize="xs">{orderInfo.orderName}</Text>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Total # of items
          </Text>
          <Text fontSize="xs">{orderInfo.lineItems.length}</Text>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Customer
          </Text>
          <Text fontSize="xs">{orderInfo.customerName}</Text>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Customer email
          </Text>
          <Text fontSize="xs">{orderInfo.customerEmail}</Text>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Delivery status
          </Text>
          <Flex gap="xs" wrap="wrap">
            {statuses}
          </Flex>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Order tags
          </Text>
          <TagList hoverToShow tags={renderedTags} />
        </Flex>
      </Flex>
    </Flex>
  );
});

// Example: str = "•••• •••• •••• 1234" returns "1234"
const extractNumbers = (str: string): string | null => {
  const numbers = str.match(/\d+$/);
  return numbers ? numbers[0] : null;
};

// TODO (jaden): add more payment method icons
const PaymentMethodIcons: Record<string, JSXElementConstructor<any>> = {
  visa: VisaIcon,
  mastercard: MastercardIcon,
  discover: DiscoverIcon,
};

export const PaymentSummary = memo(function PaymentSummary({
  billingAddress,
  transactions,
  totalLineItemsPrice,
  totalDiscounts,
  totalShippingPrice,
  totalTax,
  totalPrice,
  createdAt,
  landedCost,
  currency,
}: {
  billingAddress?: ShopifyAddress;
  transactions: Transaction[];
  totalLineItemsPrice: string;
  totalDiscounts: string;
  totalShippingPrice: string;
  totalTax: string;
  totalPrice: string;
  createdAt: string;
  landedCost?: { duties: number; taxes: number; fees: number };
  /** The order's presentment currency — the currency these amounts are in. */
  currency?: string | null;
}) {
  const saleTransactions = transactions.filter((transaction) =>
    ["sale", "capture"].includes(transaction.kind),
  );

  // Landed cost is baked into the paid shipping line; subtract it back out so
  // Shipping shows the actual charge and landed-cost rows show the rest.
  const landedCostTotal = landedCost
    ? landedCost.duties + landedCost.taxes + landedCost.fees
    : 0;
  const shippingExcludingLandedCost = Math.max(
    0,
    Number(totalShippingPrice) - landedCostTotal,
  );

  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      pr="sm"
    >
      <Flex flexDirection="column" gap="xl" p="lg" w="full">
        <Text fontSize="sm">Payment summary</Text>
        <Flex flexDirection="column">
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            flexDirection="column"
            pb="md"
          >
            <Flex flexDirection="row" justify="space-between">
              <Text fontSize="xs" textColor="tertiary">
                Products
              </Text>
              <Text fontSize="xs">
                <MoneyText
                  struck
                  value={toMoney({ amount: totalLineItemsPrice, currency })}
                />
              </Text>
            </Flex>
            {Number(totalDiscounts) > 0 && (
              <Flex flexDirection="row" justify="space-between">
                <Text fontSize="xs" textColor="tertiary">
                  Discounts
                </Text>
                <Text fontSize="xs">
                  -
                  <MoneyText
                    struck
                    value={toMoney({ amount: totalDiscounts, currency })}
                  />
                </Text>
              </Flex>
            )}
            <Flex flexDirection="row" justify="space-between">
              <Text fontSize="xs" textColor="tertiary">
                Shipping
              </Text>
              <Text fontSize="xs">
                <MoneyText
                  struck
                  value={toMoney({
                    amount: shippingExcludingLandedCost,
                    currency,
                  })}
                />
              </Text>
            </Flex>
            <Flex flexDirection="row" justify="space-between">
              <Text fontSize="xs" textColor="tertiary">
                Estimated tax
              </Text>
              <Text fontSize="xs">
                <MoneyText
                  struck
                  value={toMoney({ amount: totalTax, currency })}
                />
              </Text>
            </Flex>
            {landedCost && (
              <>
                <Flex flexDirection="row" justify="space-between">
                  <Text fontSize="xs" textColor="tertiary">
                    Duties
                  </Text>
                  <Text fontSize="xs">
                    <MoneyText
                      struck
                      value={toMoney({ amount: landedCost.duties, currency })}
                    />
                  </Text>
                </Flex>
                <Flex flexDirection="row" justify="space-between">
                  <Text fontSize="xs" textColor="tertiary">
                    Taxes and fees
                  </Text>
                  <Text fontSize="xs">
                    <MoneyText
                      struck
                      value={toMoney({
                        amount: landedCost.taxes + landedCost.fees,
                        currency,
                      })}
                    />
                  </Text>
                </Flex>
              </>
            )}
          </Flex>
          <Flex justify="space-between">
            <Text fontSize="xs" fontWeight="semibold">
              Total
            </Text>
            <Text fontSize="xs" fontWeight="semibold">
              <MoneyText
                struck
                value={toMoney({ amount: totalPrice, currency })}
              />
            </Text>
          </Flex>
        </Flex>
        <Flex flexDirection="column" gap="xs" justify="space-between">
          <Text fontSize="xs" textColor="secondary">
            Payment method
          </Text>
          <PaymentLabel transactions={saleTransactions} />
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Payment date
          </Text>
          <Text fontSize="xs">
            {getDateString(
              new Date(saleTransactions?.[0]?.processed_at ?? createdAt),
            )}
          </Text>
        </Flex>
        <Flex flexDirection="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Billing address
          </Text>
          <Flex dir="column" fontSize="sm" gap="none">
            {billingAddress?.address1 && (
              <Text fontSize="xs">{billingAddress.address1}</Text>
            )}
            {billingAddress?.address2 && (
              <Text fontSize="xs">{billingAddress.address2}</Text>
            )}
            {(billingAddress?.city ||
              billingAddress?.province ||
              billingAddress?.zip) && (
              <Text fontSize="xs">
                {billingAddress.city}
                {billingAddress.province &&
                  `, ${getProvinceCode(billingAddress.province, billingAddress.country ?? "") || billingAddress.province}`}
              </Text>
            )}
            <Text fontSize="xs">
              {billingAddress?.zip &&
                `${billingAddress.zip}${billingAddress?.country ? ", " : ""}`}
              {billingAddress?.country &&
                (getCountryCode(billingAddress.country) ??
                  billingAddress.country)}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

export const OrderItems = memo(function OrderItems({
  lineItems,
  currency,
}: {
  lineItems: LineItem[];
  /** The order's presentment currency — the currency line-item prices are in. */
  currency?: string | null;
}) {
  return (
    <Flex
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      pr="sm"
    >
      <Flex flexDirection="column" gap="xl" p="lg" w="full">
        <Text fontSize="sm">Order items ({lineItems.length})</Text>
        {lineItems.map((lineItem, i) => {
          return (
            <Flex flexDirection="column" gap="xxs" key={i}>
              <Flex flexDirection="row" justify="space-between">
                <Text className={styles.ellipsesOverflow} fontSize="xs">
                  {lineItem.title}
                </Text>
                <Text fontSize="xs">
                  <MoneyText
                    struck
                    value={toMoney({ amount: lineItem.price, currency })}
                  />
                </Text>
              </Flex>
              <Flex flexDirection="row" justify="space-between">
                <Text fontSize="xs" textColor="tertiary">
                  {(lineItem.variant_title || "").split(" / ").join(", ")}
                </Text>
                <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
                  {lineItem.quantity}
                </Text>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
});

export const PaymentLabel = memo(function PaymentLabel({
  transactions,
}: {
  transactions: Transaction[];
}) {
  const saleTransactions = transactions.filter((transaction) =>
    ["sale", "capture"].includes(transaction.kind),
  );
  return (
    <>
      {saleTransactions.map((transaction: Transaction, i: number) => {
        const PaymentMethodIcon = PaymentMethodIcons?.[
          transaction?.payment_details?.credit_card_company?.toLowerCase() || ""
        ]
          ? PaymentMethodIcons?.[
              transaction?.payment_details?.credit_card_company?.toLowerCase() ||
                ""
            ]
          : CreditCardIcon;

        return (
          <Text fontSize="sm" key={i}>
            {transaction?.payment_method &&
            transaction?.payment_details?.credit_card_company &&
            transaction?.payment_details?.credit_card_number ? (
              <Flex gap="none">
                <Flex pr="sm">
                  {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
                  <PaymentMethodIcon height={16} width={22.5} />
                </Flex>
                <Text fontSize="xs">
                  {`Ending ${extractNumbers(
                    transaction?.payment_details?.credit_card_number ?? "",
                  )}`}
                </Text>
                <Text fontSize="xs" textColor="placeholder">
                  {" - "}
                  <MoneyText
                    struck
                    value={toMoney({
                      amount: transaction.amount,
                      currency: transaction.currency,
                    })}
                  />
                </Text>
              </Flex>
            ) : transaction?.gateway ? (
              <Flex gap="none">
                <Text fontSize="xs">
                  {`${transaction.gateway.replace(/(^|_)./g, (s: string) => s.replace("_", " ").toUpperCase())}`}
                </Text>
                <Text fontSize="xs" textColor="placeholder">
                  {" - "}
                  <MoneyText
                    struck
                    value={toMoney({
                      amount: transaction.amount,
                      currency: transaction.currency,
                    })}
                  />
                </Text>
              </Flex>
            ) : (
              <Flex gap="none">
                <Text fontSize="xs">Unknown source</Text>
                <Text fontSize="xs" textColor="placeholder">
                  {" - "}
                  <MoneyText
                    struck
                    value={toMoney({
                      amount: transaction.amount,
                      currency: transaction.currency,
                    })}
                  />
                </Text>
              </Flex>
            )}
          </Text>
        );
      })}
    </>
  );
});
